// UI strings for the actors' show clips ("näyttelijän ääntämys", src/showclips/): the play button
// on a show line, the note on a clip whose place is not checked, "oikea kohta" / "väärä kohta",
// and the actor's pronunciation notes (where the actor deviates from Peterson: Peterson's own
// documented corrections, and the automatic check's uncertain notes that the users confirm or
// dismiss). Finnish (default) and English side by side; en has the same shape. Style:
// docs/FINNISH-STYLE.md. An actor's clip is listening material, never a model: Peterson's
// recording is the model.
import { defineArea } from '../area';

export const actorClipsFi = {
  /** The play button on a line that has an actor's clip. */
  play: 'Näyttelijän ääntämys',
  pause: 'Tauko',
  /** A role-play line's own button for the actor's clip (replayable, 2026-10-13). */
  short: 'Näyttelijä',
  /** Beside the speaker in Kuuntele's scenes, when the line plays the actor's clip. */
  lineTag: 'näyttelijän ääntämys',
  /** Beside the button: listening only, never the model. */
  listenOnly: 'vain kuunteluun',
  listenOnlyHint: 'Näyttelijät eivät aina ääntäneet valyriaa oikein. Malliksi kelpaa vain Petersonin äänite.',
  aria: (hv: string) => `Näyttelijän ääntämys: ${hv}`,
  failed: 'Leikettä ei voitu toistaa. Tarkista yhteys tai lataa äänet laitteelle: Asetukset → Ääni.',
  /** A clip whose place in the episode nobody has checked yet. */
  unverified: 'Kohtaa ei ole tarkistettu: leike voi olla väärästä kohdasta.',
  right: 'Oikea kohta',
  wrong: 'Väärä kohta',
  confirmed: 'Merkitty oikeaksi kohdaksi.',
  wrongNoteLabel: 'Mikä leikkeessä on vialla? (vapaaehtoinen)',
  wrongPlaceholder: 'Esim. puhuja on eri tai repliikki alkaa kesken',
  send: 'Piilota ja merkitse',
  cancel: 'Peru',
  hidden: 'Leike piilotettiin ja merkittiin tarkistettavaksi.',
  queued: 'Tallentuu, kun yhteys palaa.',
  /** The actor's pronunciation notes under the play button (npm run show:analyse). */
  notes: {
    label: 'Missä näyttelijä ääntää toisin kuin Peterson',
    /** The note's heading word, before a colon. */
    kinds: {
      stress: 'Paino',
      'vowel-length': 'Vokaalin pituus',
      vowel: 'Vokaali',
      consonant: 'Konsonantti',
      other: 'Virhe repliikissä',
    } as Record<string, string>,
    /** "näyttelijä painottaa ensimmäistä tavua" (the position in the partitive). */
    stressHeard: (position: string) => `näyttelijä painottaa ${position} tavua`,
    /** Syllable positions in the partitive, from the start … */
    positions: ['ensimmäistä', 'toista', 'kolmatta', 'neljättä', 'viidettä', 'kuudetta'],
    /** … and from the end. */
    last: 'viimeistä',
    secondLast: 'toiseksi viimeistä',
    shortened: (vowel: string) => `näyttelijä ääntää pitkän ${vowel}:n lyhyenä`,
    lengthened: (vowel: string) => `näyttelijä ääntää lyhyen ${vowel}:n pitkänä`,
    yAsI: 'näyttelijä ääntää y:n kuin i:n',
    /** Before the right form: "– oikein: ke-LĪ-ton". */
    right: 'oikein:',
    /** A documented error in the line: "sarjassa mērpī – oikein: mērprī". */
    aired: 'sarjassa',
    /** The small play button after the right form: Peterson's recording of the line. */
    listen: 'Peterson',
    listenAria: (hv: string) => `Kuuntele Petersonin ääntämys: ${hv}`,
    source: { peterson: 'Peterson', wiki: 'Wikin alaviite', auto: 'automaattinen · epävarma' } as Record<string, string>,
    sourceLink: 'lähde',
    /** An automatic note one of you confirmed. */
    confirmed: 'vahvistettu',
    confirm: 'Pitää paikkansa',
    dismiss: 'Ei pidä',
    dismissed: 'Huomautus piilotettiin.',
    why: 'Miksi?',
    whyStress: 'Paino lasketaan sanan lopusta: yleensä se on toiseksi viimeisellä tavulla.',
    whyLength: 'Viiva merkitsee pitkää vokaalia, kuten suomen aa. Pituus voi erottaa sanoja.',
    whyY: 'Valyrian y on suomen y, ei i.',
    grammarStress: 'Kielioppi: paino',
    grammarSounds: 'Kielioppi: äänteet',
    /** Beside the play button when a note stands confirmed. */
    warnPronunciation: 'ääntämisvirhe',
    warnText: 'virhe repliikissä',
  },
  /** Asetukset → Äänet laitteelle: the actors' clips go along with the other recordings. */
  offlineHave: (n: number, of: number) =>
    n === of
      ? of === 1
        ? 'Ainoa näyttelijöiden leike on tällä laitteella.'
        : `Kaikki ${of} näyttelijöiden leikettä ovat tällä laitteella.`
      : `Näyttelijöiden leikkeitä tällä laitteella ${n}/${of}.`,
};

export const actorClipsEn: typeof actorClipsFi = {
  play: "Actor's pronunciation",
  pause: 'Pause',
  short: 'Actor',
  lineTag: "actor's pronunciation",
  listenOnly: 'listening only',
  listenOnlyHint: "The actors did not always pronounce High Valyrian right. Only Peterson's recording is a model.",
  aria: (hv: string) => `Actor's pronunciation: ${hv}`,
  failed: 'The clip could not be played. Check the connection, or download the audio to this device: Settings → Sound.',
  unverified: 'Its place has not been checked: the clip may be cut from the wrong spot.',
  right: 'Right place',
  wrong: 'Wrong place',
  confirmed: 'Marked as the right place.',
  wrongNoteLabel: "What's wrong with the clip? (optional)",
  wrongPlaceholder: 'E.g. a different speaker, or the line starts midway',
  send: 'Hide and flag',
  cancel: 'Cancel',
  hidden: 'The clip was hidden and flagged for checking.',
  queued: 'Saved when you are back online.',
  notes: {
    label: 'Where the actor deviates from Peterson',
    kinds: {
      stress: 'Stress',
      'vowel-length': 'Vowel length',
      vowel: 'Vowel',
      consonant: 'Consonant',
      other: 'Error in the line',
    },
    stressHeard: (position: string) => `the actor stresses the ${position} syllable`,
    positions: ['first', 'second', 'third', 'fourth', 'fifth', 'sixth'],
    last: 'last',
    secondLast: 'second-to-last',
    shortened: (vowel: string) => `the actor says the long ${vowel} short`,
    lengthened: (vowel: string) => `the actor says the short ${vowel} long`,
    yAsI: 'the actor says y like i',
    right: 'right:',
    aired: 'aired as',
    listen: 'Peterson',
    listenAria: (hv: string) => `Listen to Peterson's pronunciation: ${hv}`,
    source: { peterson: 'Peterson', wiki: 'Wiki footnote', auto: 'automatic · uncertain' },
    sourceLink: 'source',
    confirmed: 'confirmed',
    confirm: 'That’s right',
    dismiss: 'Not so',
    dismissed: 'The note was hidden.',
    why: 'Why?',
    whyStress: 'Stress is counted from the end of the word: usually it falls on the second-to-last syllable.',
    whyLength: 'A macron marks a long vowel, as in Finnish aa. Length can tell words apart.',
    whyY: 'High Valyrian y is the Finnish y, not i.',
    grammarStress: 'Grammar: stress',
    grammarSounds: 'Grammar: sounds',
    warnPronunciation: 'pronunciation error',
    warnText: 'error in the line',
  },
  offlineHave: (n, of) =>
    n === of
      ? of === 1
        ? "The actors' only clip is on this device."
        : `All ${of} of the actors' clips are on this device.`
      : `${n}/${of} of the actors' clips on this device.`,
};

// Messages.actorClips (i18n/area.ts: collected by area-registry.ts)
export const area = defineArea('actorClips', actorClipsFi, actorClipsEn);
declare module '../area' {
  interface AreaMessages extends Record<typeof area.key, typeof actorClipsFi> {}
}
